<script lang="ts">
	import Input from '@isoftdata/svelte-input'
	import Textarea from '@isoftdata/svelte-textarea'
	import Select from '@isoftdata/svelte-select'
	import SiteDetails from '@isoftdata/svelte-site-details'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import SaveResetButton from '@isoftdata/svelte-save-reset-button'
	import type { Store, State } from 'types/store'
	import type { SvelteAsr } from 'types/common'
	import { updateStoreMutation, autofillByZipQuery } from 'common/graphql-queries'
	import type { Mediator } from 'types/mediator'
	import { getContext } from 'svelte'
	import { makeCrudStore } from '@isoftdata/svelte-store-crud'

	const mediator = getContext<Mediator>('mediator')

	export let asr: SvelteAsr
	export let canConfigureStores: boolean = false
	export let selectedStoreUuid: string
	export let states: State[] = []
	export let stores: Store[] = []
	export let userStores: number[] = []

	const siteCrudStore = makeCrudStore<Store, 'uuid'>('uuid')

	const taxServices = [
		//Const for now until fetched from server
		{ value: 'NONE', label: 'None' },
		{ value: 'MOCK', label: 'Mock' },
		{ value: 'TAXJAR', label: 'TaxJar' },
		{ value: 'VERTEX', label: 'Vertex' },
	]
	const addressServices = [
		//Const for now until fetched from server
		{ value: 'NONE', label: 'None' },
		{ value: 'MOCK', label: 'Mock' },
		{ value: 'TAXJAR', label: 'TaxJar' },
		{ value: 'VERTEX', label: 'Vertex' },
	]

	if (import.meta.env.DEV) {
		taxServices.push({ value: 'TAXJAR_SANDBOX', label: 'TaxJar Sandbox' })
		addressServices.push({ value: 'TAXJAR_SANDBOX', label: 'TaxJar Sandbox' })
	}

	let storesChanged: boolean = false

	$: storesChanged = $siteCrudStore && siteCrudStore.updatedValues.length > 0
	$: selectedStoreIndex = stores.findIndex(store => store.uuid === selectedStoreUuid)
	$: canEditSelectedStore = getStorePermissions(stores[selectedStoreIndex]?.id)

	function getStorePermissions(storeId: number | null) {
		if (!storeId) {
			return false
		}
		return userStores.includes(storeId)
	}

	function markStoreDirty() {
		siteCrudStore.update(stores[selectedStoreIndex])
	}

	function updateRemittanceAddress() {
		if (stores[selectedStoreIndex].remittanceIsMainAddress) {
			stores[selectedStoreIndex].remittanceAddress = stores[selectedStoreIndex].street
			stores[selectedStoreIndex].remittanceCity = stores[selectedStoreIndex].city
			stores[selectedStoreIndex].remittanceState = stores[selectedStoreIndex].state
			stores[selectedStoreIndex].remittanceZip = stores[selectedStoreIndex].zip
			stores[selectedStoreIndex].remittanceCountry = stores[selectedStoreIndex].country
			stores[selectedStoreIndex].remittancePhone = stores[selectedStoreIndex].phone
			markStoreDirty()
		}
	}

	function toggleRemittanceAddress() {
		stores[selectedStoreIndex].remittanceIsMainAddress = !stores[selectedStoreIndex].remittanceIsMainAddress
		updateRemittanceAddress()
	}

	async function getLocationDataFromZip() {
		const zip = stores[selectedStoreIndex].zip
		if (!zip) {
			return
		}
		const { autofillByZip } = (await mediator.publish('graphqlFetch', autofillByZipQuery, { zipcode: zip })) as {
			autofillByZip: { city: string; stateAbbreviation: string; country: string }
		}
		if (!autofillByZip) {
			return
		}
		stores[selectedStoreIndex].city = autofillByZip.city
		stores[selectedStoreIndex].state =
			states.find(state => state.abbreviation === autofillByZip.stateAbbreviation)?.abbreviation ?? ''
		stores[selectedStoreIndex].country = autofillByZip.country

		markStoreDirty()
	}

	async function saveStore() {
		mediator.publish('showMessage', {
			heading: 'Saving...',
			message: '',
			type: 'info',
			time: false,
		})

		const storesToUpdate = siteCrudStore.updatedValues

		try {
			storesToUpdate.length > 0
				? await Promise.all(
						storesToUpdate.map(store =>
							mediator.publish('graphqlFetch', updateStoreMutation, {
								input: {
									id: store.id,
									addressService: {
										service: store.addressService.service,
										apiKey: store.addressService.apiKey,
									},
									taxService: {
										service: store.taxService.service,
										apiKey: store.taxService.apiKey,
									},
									code: store.code,
									name: store.name,
									address: store.street,
									city: store.city,
									state: store.state,
									zip: store.zip,
									country: store.country,
									phone: store.phone,
									fax: store.fax,
									remittanceAddress: store.remittanceAddress,
									remittanceCity: store.remittanceCity,
									remittanceState: store.remittanceState,
									remittanceZip: store.remittanceZip,
									remittanceCountry: store.remittanceCountry,
									remittancePhone: store.remittancePhone,
									webAddress: store.webAddress,
									invoiceMemo: store.invoiceMemo,
									private: store.private,
								},
							}),
						),
				  )
				: null
			// Set this to false to not trip `canLeaveState`
			storesChanged = false
			mediator.publish('showMessage', {
				heading: 'Saved!',
				message: 'Stores saved successfully.',
				type: 'success',
				time: 10000,
			})
			// Reload the page after a successful save, but not after an error
			asr.go(null, { lastResetTime: null, lastSavedTime: Date.now() }, { inherit: true })
		} catch (err: any) {
			let theError: Error
			// Error might be in an array
			if (Array.isArray(err) && 'message' in err[0]) {
				theError = err[0] as Error
			} else if ('message' in err) {
				theError = err as Error
			} else {
				theError = new Error('Unknown Error')
			}
			console.error(theError)
			mediator.publish('showMessage', {
				heading: 'Error Saving Stores',
				message: theError.message,
				type: 'danger',
				time: false,
			})
		}
	}

	export function canLeaveState() {
		return !storesChanged || confirm('You have unsaved changes. Are you sure you want to leave?')
	}
</script>

<div class="form-row">
	<div class="col-lg-3 col-md-3 col-sm-6 pr-1">
		<Select
			label="Select Store"
			showEmptyOption={false}
			options={stores}
			bind:value={selectedStoreUuid}
		>
			{#snippet option({ option: store })}
				<option value={store?.uuid}>{store?.code} - {store?.name}</option>
			{/snippet}
		</Select>
	</div>
	<div class="col-12 col-sm text-right">
		<SaveResetButton
			disabled={!storesChanged || !canEditSelectedStore}
			resetHref={asr.makePath(null, { lastResetTime: Date.now(), lastSavedTime: null }, { inherit: true })}
			save={() => saveStore()}
		/>
	</div>
</div>

<div class="form-row">
	<div class="col-12 col-md-4 mb-2">
		<SiteDetails
			bind:sites={stores}
			{states}
			{siteCrudStore}
			siteAlias="Store"
			canEditSelectedSite={canEditSelectedStore && canConfigureStores}
			showTimezone={false}
			bind:selectedSiteUuid={selectedStoreUuid}
			updateLocationFieldsFromZip={getLocationDataFromZip}
			hideCreateDeleteButtons={true}
			fieldChange={() => updateRemittanceAddress()}
		/>
	</div>
	<div class="col-12 col-md-4 mb-2">
		<div class="card h-100">
			<div class="card-header">
				<h5 class="mb-0">Remittance Address</h5>
			</div>
			<div class="card-body">
				<div class="form-row">
					<div class="col-12">
						<Checkbox
							disabled={!canEditSelectedStore || !canConfigureStores}
							label="Remittance address is the same as physical address"
							checked={stores[selectedStoreIndex].remittanceIsMainAddress}
							onchange={toggleRemittanceAddress}
						/>
					</div>
					<div class="col-12">
						<Input
							disabled={stores[selectedStoreIndex].remittanceIsMainAddress ||
								!canEditSelectedStore ||
								!canConfigureStores}
							label="Address"
							placeholder="Street Address"
							bind:value={stores[selectedStoreIndex].remittanceAddress}
							onchange={markStoreDirty}
						/>
					</div>
					<div class="col-md-4 col-6">
						<Input
							disabled={stores[selectedStoreIndex].remittanceIsMainAddress ||
								!canEditSelectedStore ||
								!canConfigureStores}
							label="City"
							placeholder="City"
							bind:value={stores[selectedStoreIndex].remittanceCity}
							onchange={markStoreDirty}
						/>
					</div>
					<div class="col-md-3 col-6">
						<Select
							disabled={stores[selectedStoreIndex].remittanceIsMainAddress ||
								!canEditSelectedStore ||
								!canConfigureStores}
							label="State"
							showEmptyOption={true}
							emptyValue={''}
							options={states}
							bind:value={stores[selectedStoreIndex].remittanceState}
							onchange={markStoreDirty}
						>
							{#snippet option({ option: state })}
								<option value={state?.abbreviation}>{state?.name}</option>
							{/snippet}
						</Select>
					</div>
					<div class="col-md-2 col-6">
						<Input
							disabled={stores[selectedStoreIndex].remittanceIsMainAddress ||
								!canEditSelectedStore ||
								!canConfigureStores}
							placeholder="Zip"
							label="Zip"
							bind:value={stores[selectedStoreIndex].remittanceZip}
							onchange={markStoreDirty}
						/>
					</div>
					<div class="col-md-3 col-6">
						<Input
							disabled={stores[selectedStoreIndex].remittanceIsMainAddress ||
								!canEditSelectedStore ||
								!canConfigureStores}
							placeholder="Country"
							label="Country"
							bind:value={stores[selectedStoreIndex].remittanceCountry}
							onchange={markStoreDirty}
						/>
					</div>
					<div class="col-12">
						<Input
							disabled={stores[selectedStoreIndex].remittanceIsMainAddress ||
								!canEditSelectedStore ||
								!canConfigureStores}
							label="Remittance Phone"
							placeholder="Phone#"
							bind:value={stores[selectedStoreIndex].remittancePhone}
							onchange={markStoreDirty}
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
	<div class="col-12 col-md-4 mb-2">
		<div class="card h-100">
			<div class="card-header">
				<h5 class="mb-0">Misc Details</h5>
			</div>
			<div class="card-body">
				<Input
					disabled={!canEditSelectedStore || !canConfigureStores}
					placeholder="URL"
					label="Web Address"
					bind:value={stores[selectedStoreIndex].webAddress}
					onchange={markStoreDirty}
				/>
				<Textarea
					disabled={!canEditSelectedStore || !canConfigureStores}
					label="Invoice Memo"
					bind:value={stores[selectedStoreIndex].invoiceMemo}
					onchange={markStoreDirty}
				/>
			</div>
		</div>
	</div>
</div>
<div class="form-row">
	<div class="col-12 col-md-6 mb-2">
		<div class="card">
			<div class="card-header">
				<h5 class="mb-0">Tax Service</h5>
			</div>
			<div class="card-body">
				<Select
					label="Tax Service"
					showEmptyOption={false}
					disabled={!canConfigureStores || !canEditSelectedStore}
					options={taxServices}
					bind:value={stores[selectedStoreIndex].taxService.service}
					onchange={markStoreDirty}
				>
					{#snippet option({ option: taxService })}
						<option value={taxService?.value}>{taxService?.label}</option>
					{/snippet}
				</Select>
				<Input
					label="Tax Service API Key"
					type="password"
					disabled={!canConfigureStores || !canEditSelectedStore}
					bind:value={stores[selectedStoreIndex].taxService.apiKey}
					onchange={markStoreDirty}
				/>
			</div>
		</div>
	</div>
	<div class="col-12 col-md-6">
		<div class="card">
			<div class="card-header">
				<h5 class="mb-0">Address Service</h5>
			</div>
			<div class="card-body">
				<Select
					label="Address Service"
					showEmptyOption={false}
					disabled={!canConfigureStores || !canEditSelectedStore}
					options={addressServices}
					bind:value={stores[selectedStoreIndex].addressService.service}
					onchange={markStoreDirty}
				>
					{#snippet option({ option: addressService })}
						<option value={addressService?.value}>{addressService?.label}</option>
					{/snippet}
				</Select>
				<Input
					label="Address Service API Key"
					type="password"
					disabled={!canConfigureStores || !canEditSelectedStore}
					bind:value={stores[selectedStoreIndex].addressService.apiKey}
					onchange={markStoreDirty}
				/>
			</div>
		</div>
	</div>
</div>
